import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Palette, Upload, Save, RotateCcw, Image, Type, Loader2, Check, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  logo_url: null,
  brand_name: 'AdPulse AI',
  primary_color: '#5ABAE3',
  secondary_color: '#D697C9',
  accent_color: '#B6D957',
  dark_mode: true
};

const COLOR_PRESETS = [
  { name: 'Default', primary: '#5ABAE3', secondary: '#D697C9', accent: '#B6D957' },
  { name: 'Ocean', primary: '#0ea5e9', secondary: '#6366f1', accent: '#14b8a6' },
  { name: 'Sunset', primary: '#f97316', secondary: '#ec4899', accent: '#eab308' },
  { name: 'Forest', primary: '#B6D957', secondary: '#84cc16', accent: '#10b981' },
  { name: 'Royal', primary: '#8b5cf6', secondary: '#a855f7', accent: '#6366f1' },
  { name: 'Fire', primary: '#ef4444', secondary: '#f97316', accent: '#fbbf24' }
];

export default function Branding() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (previewMode) {
      applyBranding(settings);
    } else {
      applyBranding(originalSettings);
    }
  }, [previewMode, settings]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setOriginalSettings(response.data);
      applyBranding(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyBranding = (brand) => {
    document.documentElement.style.setProperty('--color-ai-glow', brand.primary_color);
    document.documentElement.style.setProperty('--color-brand-meta', brand.secondary_color);
    document.documentElement.style.setProperty('--color-success', brand.accent_color);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOriginalSettings(settings);
      toast.success('Configuración de marca guardada');
      setPreviewMode(false);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setPreviewMode(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no puede superar 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/branding/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success('Logo subido correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir logo');
    } finally {
      setUploading(false);
    }
  };

  const applyPreset = (preset) => {
    setSettings({
      ...settings,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent
    });
    setPreviewMode(true);
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ai-glow" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="branding-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl mb-2">Marca Blanca</h1>
            <p className="text-secondary">Personaliza la apariencia de tu dashboard</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}
              className={`rounded-full ${previewMode ? 'border-ai-glow text-ai-glow' : ''}`}>
              <Eye className="w-4 h-4 mr-2" />
              {previewMode ? 'Previsualizando' : 'Vista Previa'}
            </Button>
            
            <Button variant="outline" onClick={handleReset} className="rounded-full">
              <RotateCcw className="w-4 h-4 mr-2" />
              Resetear
            </Button>
            
            <Button onClick={handleSave} disabled={saving || !hasChanges}
              className="bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-full">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo Section */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6">
              <h3 className="font-medium mb-4 flex items-center">
                <Image className="w-5 h-5 mr-2 text-ai-glow" />
                Logo de la Marca
              </h3>
              
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-white/20">
                  {settings.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Image className="w-10 h-10 text-secondary" />
                  )}
                </div>
                
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload}
                    accept="image/*" className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    variant="outline" className="rounded-full mb-2">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Subir Logo
                  </Button>
                  <p className="text-xs text-secondary">PNG, JPG o SVG. Máximo 2MB.</p>
                </div>
              </div>
            </motion.div>

            {/* Brand Name */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="glass-card p-6">
              <h3 className="font-medium mb-4 flex items-center">
                <Type className="w-5 h-5 mr-2 text-ai-glow" />
                Nombre de la Marca
              </h3>
              <input type="text" value={settings.brand_name}
                onChange={(e) => { setSettings({ ...settings, brand_name: e.target.value }); setPreviewMode(true); }}
                placeholder="AdPulse AI"
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-lg" />
            </motion.div>

            {/* Colors */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="glass-card p-6">
              <h3 className="font-medium mb-4 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-ai-glow" />
                Colores de la Marca
              </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm text-secondary mb-2">Color Primario</label>
                  <div className="flex items-center space-x-2">
                    <input type="color" value={settings.primary_color}
                      onChange={(e) => { setSettings({ ...settings, primary_color: e.target.value }); setPreviewMode(true); }}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0" />
                    <input type="text" value={settings.primary_color}
                      onChange={(e) => { setSettings({ ...settings, primary_color: e.target.value }); setPreviewMode(true); }}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg p-2 text-sm font-mono" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-secondary mb-2">Color Secundario</label>
                  <div className="flex items-center space-x-2">
                    <input type="color" value={settings.secondary_color}
                      onChange={(e) => { setSettings({ ...settings, secondary_color: e.target.value }); setPreviewMode(true); }}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0" />
                    <input type="text" value={settings.secondary_color}
                      onChange={(e) => { setSettings({ ...settings, secondary_color: e.target.value }); setPreviewMode(true); }}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg p-2 text-sm font-mono" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-secondary mb-2">Color Acento</label>
                  <div className="flex items-center space-x-2">
                    <input type="color" value={settings.accent_color}
                      onChange={(e) => { setSettings({ ...settings, accent_color: e.target.value }); setPreviewMode(true); }}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0" />
                    <input type="text" value={settings.accent_color}
                      onChange={(e) => { setSettings({ ...settings, accent_color: e.target.value }); setPreviewMode(true); }}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg p-2 text-sm font-mono" />
                  </div>
                </div>
              </div>

              {/* Color Presets */}
              <div>
                <label className="block text-sm text-secondary mb-3">Paletas Predefinidas</label>
                <div className="grid grid-cols-6 gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button key={preset.name} onClick={() => applyPreset(preset)}
                      className="p-2 rounded-lg border border-white/10 hover:border-white/30 transition-all text-center"
                      title={preset.name}>
                      <div className="flex justify-center space-x-1 mb-1">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                      </div>
                      <p className="text-xs">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Preview Panel */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="glass-card p-6 h-fit sticky top-8">
            <h3 className="font-medium mb-4">Vista Previa</h3>
            
            <div className="rounded-xl bg-black/50 p-4 space-y-4">
              {/* Header Preview */}
              <div className="flex items-center space-x-3">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: settings.primary_color }} />
                )}
                <span className="font-bold" style={{ color: settings.primary_color }}>
                  {settings.brand_name || 'AdPulse AI'}
                </span>
              </div>
              
              {/* Button Preview */}
              <div className="flex space-x-2">
                <button className="px-4 py-2 rounded-full text-white text-sm font-medium"
                  style={{ background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.secondary_color})` }}>
                  Botón Primario
                </button>
                <button className="px-4 py-2 rounded-full text-sm font-medium border"
                  style={{ borderColor: settings.primary_color, color: settings.primary_color }}>
                  Secundario
                </button>
              </div>
              
              {/* Stats Preview */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-lg font-bold" style={{ color: settings.primary_color }}>$2.5K</p>
                  <p className="text-xs text-secondary">Gasto</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-lg font-bold" style={{ color: settings.secondary_color }}>1.2K</p>
                  <p className="text-xs text-secondary">Clicks</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-lg font-bold" style={{ color: settings.accent_color }}>+24%</p>
                  <p className="text-xs text-secondary">ROI</p>
                </div>
              </div>
              
              {/* Progress Preview */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Progreso</span>
                  <span>75%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className="h-full rounded-full w-3/4"
                    style={{ background: `linear-gradient(90deg, ${settings.primary_color}, ${settings.secondary_color})` }} />
                </div>
              </div>
            </div>
            
            {hasChanges && (
              <p className="text-xs text-warning mt-4 flex items-center">
                <Check className="w-3 h-3 mr-1" />
                Cambios sin guardar
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
